import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";
import { useHeadlinesFilterStore } from "./HeadlinesFilterStore";
import { useEffect, useRef } from "react";

/**
 * Hook that tracks portfolios with sound alerts enabled for Headlines.
 * Reads from the global HeadlinesFilterStore and filters based on:
 * 1. User's watchlist alert mode setting (off/highlight/filter/both)
 * 2. Whether the portfolio has sound notifications enabled
 *
 * Returns a ref that can be used in SSE callbacks without causing re-renders.
 */
export function useHeadlinesPortfoliosWithSoundEnabled() {
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistAlertMode } = useWatchlistAlertMode();

  const portfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.portfoliosHighlight
  );
  const portfoliosFilter = useHeadlinesFilterStore(
    (state) => state.portfoliosFilter
  );

  // Compute portfolios with sound enabled based on mode
  let portfolioIds: number[] = [];

  if (watchlistAlertMode === "off") {
    portfolioIds = [];
  } else if (watchlistAlertMode === "highlight") {
    portfolioIds = portfoliosHighlight;
  } else if (watchlistAlertMode === "filter") {
    portfolioIds = portfoliosFilter;
  } else if (watchlistAlertMode === "both") {
    // Combine and deduplicate
    portfolioIds = [...new Set([...portfoliosHighlight, ...portfoliosFilter])];
  }

  // Map to portfolios and filter for sound enabled
  const portfoliosWithSoundEnabled = portfolioIds
    .map((portfolioId) =>
      portfolios.find((portfolio) => portfolio.id === portfolioId)
    )
    .filter(
      (portfolio): portfolio is Portfolio =>
        portfolio !== undefined && portfolio.notifications.sound
    );

  // Use ref for stable reference in SSE callbacks
  const portfoliosWithSoundEnabledRef = useRef(portfoliosWithSoundEnabled);

  useEffect(() => {
    portfoliosWithSoundEnabledRef.current = portfoliosWithSoundEnabled;
  }, [portfoliosWithSoundEnabled]);

  return portfoliosWithSoundEnabledRef;
}
